<template>
  <div class="group rounded-lg px-3 py-3 transition-colors duration-100 hover:bg-background-normal">
    <div class="flex items-start gap-3">
      <!-- Status -->
      <div
        class="flex-shrink-0 w-6 h-6 mt-0.5 rounded-full border-2 flex items-center justify-center transition-colors duration-100"
        :class="statusClass"
      >
        <svg
          v-if="showPadlock"
          xmlns="http://www.w3.org/2000/svg"
          width="11"
          height="11"
          viewBox="0 0 12 12"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <rect
            x="2.25"
            y="5.25"
            width="7.5"
            height="5.5"
            rx="1.25"
          />
          <path d="M4 5.25V3.75a2 2 0 0 1 4 0v1.5" />
        </svg>
        <svg
          v-else-if="step.skipped"
          xmlns="http://www.w3.org/2000/svg"
          width="10"
          height="10"
          viewBox="0 0 12 12"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
        >
          <path d="M3 6h6" />
        </svg>
        <svg
          v-else
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path d="M2 6.5l2.5 2.5L10 3.5" />
        </svg>
      </div>

      <!-- Title, tags & hint -->
      <div class="flex-grow min-w-0">
        <div class="flex items-center gap-1.5 flex-wrap">
          <BaseText
            type="label"
            size="sm"
            :line-through="step.done"
            :class="step.done || step.skipped || step.locked ? 'text-text-subdued' : 'text-text-loud'"
          >
            {{ step.content.title }}
          </BaseText>
          <div
            v-if="needsUpgrade"
            class="flex items-center gap-1 pl-1 pr-1.5 py-0.5 rounded bg-neutral-25"
          >
            <LockedIcon
              :width="12"
              :height="12"
              class="flex-shrink-0 text-icon-muted"
            />
            <BaseText
              type="label"
              size="xs"
              class="text-text-muted"
            >
              {{ productLockedCopy.badgeLabel }}
            </BaseText>
          </div>
          <BaseTooltipV2
            v-if="scopeTag"
            :text="scopeTag.tooltip"
            position="top"
            alignment="start"
          >
            <div class="px-1.5 py-px rounded border border-border-normal">
              <BaseText
                type="label"
                size="xs"
                class="text-text-subdued"
              >
                {{ scopeTag.label }}
              </BaseText>
            </div>
          </BaseTooltipV2>
        </div>
        <BaseText
          type="body"
          size="sm"
          class="text-text-subdued mt-0.5"
        >
          {{ hint }}
          <button
            v-if="step.content.why && !step.locked"
            class="inline underline transition-colors duration-100 hover:text-text-muted"
            @click="toggleWhy"
          >
            {{ whyOpen ? 'Hide' : 'Why?' }}
          </button>
        </BaseText>
      </div>

      <!-- CTA, with the subdued skip control underneath -->
      <div class="flex-shrink-0 flex flex-col items-end gap-1.5">
        <div
          v-if="step.skipped"
          class="px-2 py-0.5 rounded-md bg-neutral-25"
        >
          <BaseText
            type="label"
            size="xs"
            class="text-text-muted"
          >
            {{ skipCopy.skippedLabel }}
          </BaseText>
        </div>
        <BaseButton
          v-else-if="needsUpgrade"
          outlined
          class="w-40"
          @click="$emit('cta', step)"
        >
          <span class="flex items-center justify-center gap-1.5">
            <UpgradeIcon
              class="flex-shrink-0 text-icon-normal"
              :width="16"
              :height="16"
            />
            {{ productLockedCopy.ctaLabel }}
          </span>
        </BaseButton>
        <BaseButton
          v-else-if="ctaLabel"
          outlined
          class="w-40"
          :disabled="step.locked"
          @click="$emit('cta', step)"
        >
          <span class="flex items-center justify-center gap-1.5">
            <component
              :is="ctaIcon"
              v-if="ctaIcon"
              class="flex-shrink-0 text-icon-normal"
              :width="16"
              :height="16"
            />
            {{ ctaLabel }}
          </span>
        </BaseButton>
        <BaseText
          v-if="canSkip"
          type="label"
          size="xs"
          class="text-text-disabled"
        >
          <button
            class="inline px-1 transition-colors duration-100 hover:text-text-muted disabled:cursor-default"
            :disabled="skipping"
            @click="$emit('skip', step)"
          >
            {{ step.skipped ? skipCopy.undoLabel : skipCopy.skipLabel }}
          </button>
        </BaseText>
      </div>
    </div>

    <!-- "Why?" disclosure -->
    <BaseHeightTransition>
      <div
        v-if="whyOpen"
        class="overflow-hidden"
      >
        <div class="mt-2 ml-9 px-3 py-2 rounded-md bg-neutral-10 transition-colors duration-100 group-hover:bg-neutral-50">
          <BaseText
            type="body"
            size="sm"
            class="text-text-muted"
          >
            {{ step.content.why }}
          </BaseText>
        </div>
      </div>
    </BaseHeightTransition>
  </div>
</template>

<script>
import posthog from 'posthog-js'
import { SETUP_PRODUCT_LOCKED, SETUP_SCOPE_TAGS, SETUP_SKIP } from '@/utils/setup/setupContent'
import LockedIcon from '../globals/Icons/LockedIcon.vue'
import UpgradeIcon from '../globals/Icons/SidebarIcons/UpgradeIcon.vue'
import ChromeIcon from '../globals/Icons/OnboardIcons/ChromeIcon.vue'
import AddUserIcon from '../globals/Icons/AddUserIcon.vue'
import SpyderIcon from '../globals/Icons/SidebarIcons/SpyderIcon.vue'
import AdRankIcon from '../globals/Icons/SpyderModeIcons/AdRankIcon.vue'
import SaveAdIcon from '../globals/Icons/SaveAdIcon.vue'
import NewBoardIcon from '../globals/Icons/NewBoardIcon.vue'
import SearchIcon from '../globals/Icons/SearchIcon.vue'
import MonoInstagramIcon from '../globals/Icons/socials/mono/MonoInstagramIcon.vue'
import MetaIcon from '../globals/Icons/OnboardIcons/MetaIcon.vue'
import MCPIcon from '../globals/Icons/ApiIcons/MCPIcon.vue'
import CreatePerformanceReportItemIcon from '../globals/Icons/SidebarIcons/dropdowns/CreatePerformanceReportItemIcon.vue'
import CreateComparativeReportItemIcon from '../globals/Icons/SidebarIcons/dropdowns/CreateComparativeReportItemIcon.vue'

// `icon` keys used in setupContent.js
const CTA_ICONS = {
  chrome: ChromeIcon,
  inviteUser: AddUserIcon,
  spyder: SpyderIcon,
  adRank: AdRankIcon,
  save: SaveAdIcon,
  newBoard: NewBoardIcon,
  search: SearchIcon,
  instagram: MonoInstagramIcon,
  meta: MetaIcon,
  mcp: MCPIcon,
  topPerforming: CreatePerformanceReportItemIcon,
  comparison: CreateComparativeReportItemIcon
}

export default {
  name: 'SetupTaskRow',
  components: { LockedIcon, UpgradeIcon },
  props: {
    // Resolved step from SetupModule/getSetupSteps merged with its `content`.
    // Locked steps keep their own CTA, disabled until the prerequisite is done.
    step: {
      type: Object,
      required: true
    },
    // A skip / undo for this step is in flight
    skipping: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      productLockedCopy: SETUP_PRODUCT_LOCKED,
      skipCopy: SETUP_SKIP,
      whyOpen: false
    }
  },
  computed: {
    needsUpgrade () {
      return this.step.productLocked && !this.step.done
    },
    showPadlock () {
      return !this.step.done && !this.step.skipped && (this.step.locked || this.needsUpgrade)
    },
    // Done, plan-locked and the signup email step have nothing to skip
    canSkip () {
      return !this.step.done && !this.needsUpgrade && this.step.id !== 'email_verified'
    },
    statusClass () {
      if (this.step.done) return 'bg-primary-green-100 border-primary-green-100 text-white'
      if (this.step.skipped) return 'bg-neutral-25 border-neutral-50 text-icon-muted'
      if (this.showPadlock) return 'bg-neutral-25 border-neutral-50 text-icon-disabled'
      return 'bg-white border-neutral-100 text-transparent'
    },
    hint () {
      const { content } = this.step
      if (this.needsUpgrade) return content.upgradeHint || SETUP_PRODUCT_LOCKED.hint
      if (this.step.skipped) return content.hint
      if (this.step.locked && content.lockedHint) return content.lockedHint
      return content.hint
    },
    ctaLabel () {
      const { content } = this.step
      if (this.step.done || this.step.skipped || !content.cta) return null
      return content.cta.label
    },
    ctaIcon () {
      return CTA_ICONS[this.step.content.icon] || null
    },
    scopeTag () {
      if (this.step.done || this.step.skipped || this.step.id === 'email_verified') return null
      return SETUP_SCOPE_TAGS[this.step.content.tagScope || this.step.scope] || null
    }
  },
  methods: {
    toggleWhy () {
      this.whyOpen = !this.whyOpen
      if (this.whyOpen) posthog.capture('Setup Why Opened', { step_id: this.step.id, section: this.step.section })
    }
  }
}
</script>
